import { toDateValue } from './relativeTime';

/**
 * Logique PURE de l'onglet « Emails » (fiche client du panneau admin) — aucune dépendance Firebase
 * ni React : tout est vérifiable par `react-scripts test` sans émulateur (DI2a-11, premier test du
 * dépôt `msd_admin`).
 *
 * Vocabulaire : l'écran dit « Emails » (le mot du demandeur), le code dit `mail` (le nom de la
 * collection Firestore). Les libellés TRADUISENT le code du serveur ; quand un verdict est inconnu,
 * le code brut est affiché — l'écran traduit, il n'invente pas (doctrine des écrans existants).
 *
 * Ce que la mesure live C12 (09/10/2026, 326 documents) a fixé ici :
 *   · `delivery` présent sur 100 % (308 SUCCESS / 18 ERROR) ⇒ le verdict est exploitable ;
 *   · `meta.userId` sur 264 (81 %) ⇒ 62 envois anciens resteront invisibles (note d'écran) ;
 *   · `meta.environment` : 250 `production`, 76 absents (23,3 %) ⇒ « non tracé », jamais « test » ;
 *   · `to` est à la RACINE du document (jamais dans `message`) et `message.text` n'existe pas ;
 *   · `message.html` : 100 %, 8,4 Kio en moyenne, 13,7 Kio au maximum.
 */

/**
 * Nombre maximum d'envois lus pour une fiche (modèle `CLIENTS_LIMIT` de `clientService`).
 * Mesure C12 : le client le plus chargé compte 36 envois ⇒ cette borne ne tronque rien aujourd'hui.
 */
export const MAILS_LIMIT = 200;

/** Filtres de statut proposés à l'écran (DI2a-8). */
export const STATUT_FILTRES = [
  { id: 'tous', label: 'Tous les statuts' },
  { id: 'echecs', label: 'Échecs seulement' },
  { id: 'envoyes', label: 'Envoyés seulement' }
];

/** Filtres d'environnement proposés à l'écran (DI2a-8). */
export const ENVIRONNEMENT_FILTRES = [
  { id: 'tous', label: 'Tous les environnements' },
  { id: 'production', label: 'Production' },
  { id: 'hors-production', label: 'Hors production (dont non tracés)' }
];

/**
 * Motifs d'ADRESSE DE TEST (décision DI5-6, constat C2) — volontairement peu nombreux, nommés et
 * testables (banc B5). Un envoi dont un destinataire tombe sur un motif est ÉTIQUETÉ « adresse de
 * test » et compté À PART — jamais masqué : un vrai client dont l'adresse ressemblerait à un motif
 * ne doit pas devenir invisible.
 *
 * Les motifs portent sur le DOMAINE (partie après le dernier « @ ») : le préfixe « test. » ne doit
 * pas attraper « contact@contest.com », qui est une adresse légitime.
 */
export const MOTIFS_ADRESSE_TEST = [
  { id: 'mail7845', cible: 'domaine', motif: 'mail7845.com' },
  { id: 'example', cible: 'domaine', motif: 'example.com' },
  { id: 'local', cible: 'suffixe', motif: '.local' },
  { id: 'test', cible: 'prefixe', motif: 'test.' }
];

/** Domaine d'une adresse (partie après le DERNIER « @ »), en minuscules ; chaîne vide si absente. */
export const domaineAdresse = (adresse) => {
  if (typeof adresse !== 'string') return '';
  const arobase = adresse.lastIndexOf('@');
  const domaine = arobase === -1 ? adresse : adresse.slice(arobase + 1);
  return domaine.trim().toLowerCase();
};

/**
 * Motif de test reconnu pour une adresse (`DI5-6`), ou `null` si aucun ne correspond.
 * @param {string} adresse - Adresse e-mail
 * @returns {string|null} Identifiant du motif
 */
export const motifAdresseTest = (adresse) => {
  const domaine = domaineAdresse(adresse);
  if (!domaine) return null;
  for (const motif of MOTIFS_ADRESSE_TEST) {
    if (motif.cible === 'domaine' && domaine === motif.motif) return motif.id;
    if (motif.cible === 'suffixe' && domaine.endsWith(motif.motif)) return motif.id;
    if (motif.cible === 'prefixe' && domaine.startsWith(motif.motif)) return motif.id;
  }
  return null;
};

/** L'adresse est-elle une adresse de test reconnue ? (banc B5) */
export const estAdresseDeTest = (adresse) => motifAdresseTest(adresse) !== null;

/** Un envoi est « de test » dès qu'UN de ses destinataires est une adresse de test. */
export const envoiDeTest = (to) => destinatairesEnvoi(to).some((adresse) => estAdresseDeTest(adresse));

/**
 * Nombre d'envois « de test » d'une liste — compté À PART du total affiché (banc B5).
 * @param {Array<Object>} docs - Envois mappés
 * @returns {number}
 */
export const compterEnvoisDeTest = (docs) => (Array.isArray(docs) ? docs : [])
  .filter((doc) => envoiDeTest(doc && doc.to)).length;

/**
 * L'écran doit-il DIRE que la liste est bornée (banc B8) ? À la borne atteinte, la complétude n'est
 * pas prouvable ⇒ on l'avoue plutôt que de laisser croire à une liste exhaustive.
 */
export const listeTronquee = (docs, limite = MAILS_LIMIT) => (Array.isArray(docs) ? docs.length : 0) >= limite;

const CLASSE_NEUTRE = 'bg-base-300 text-base-content border border-base-300';
const CLASSE_SUCCES = 'bg-success/20 text-success border border-success/30';
const CLASSE_ECHEC = 'bg-error/20 text-error border border-error/30';
const CLASSE_ATTENTE = 'bg-warning/20 text-warning border border-warning/30';
const CLASSE_INFO = 'bg-info/20 text-info border border-info/30';

/**
 * Verdicts rendus par l'extension « Trigger Email » dans `delivery.state`.
 * `ton` sert au filtrage : `succes`, `echec`, `attente`.
 */
const VERDICTS = {
  SUCCESS: { label: 'envoyé', classe: CLASSE_SUCCES, ton: 'succes' },
  ERROR: { label: 'échec', classe: CLASSE_ECHEC, ton: 'echec' },
  PENDING: { label: 'en attente', classe: CLASSE_ATTENTE, ton: 'attente' },
  PROCESSING: { label: "en cours d'envoi", classe: CLASSE_INFO, ton: 'attente' },
  RETRY: { label: 'nouvelle tentative prévue', classe: CLASSE_ATTENTE, ton: 'attente' }
};

/**
 * Traduit le verdict d'un envoi.
 *
 * Règles (mesure C12) :
 *   · `delivery` absent ⇒ « en attente d'envoi », JAMAIS « échec » (l'extension n'est pas passée) ;
 *   · `state` inconnu (valeur future de l'extension) ⇒ le CODE BRUT est affiché, jamais « — ».
 * @param {Object|null} delivery - Champ `delivery` du document `mail`
 * @returns {{code: (string|null), label: string, classe: string, ton: string, erreur: (string|null)}}
 */
export const traduireVerdict = (delivery) => {
  const state = delivery && typeof delivery.state === 'string' && delivery.state.trim()
    ? delivery.state
    : null;
  // La cause d'un échec est REMONTÉE telle quelle (message de l'extension), jamais reformulée :
  // c'est elle qui dit au support s'il s'agit d'un domaine inconnu ou d'un gabarit incomplet.
  const erreur = delivery && typeof delivery.error === 'string' && delivery.error.trim()
    ? delivery.error
    : null;

  if (!state) {
    return {
      code: null,
      label: delivery ? 'statut d’envoi non tracé' : 'en attente d’envoi',
      classe: CLASSE_NEUTRE,
      ton: 'attente',
      erreur
    };
  }

  const connu = VERDICTS[state];
  if (connu) return { code: state, ...connu, erreur };

  return { code: state, label: `${state} (non traduit)`, classe: CLASSE_ATTENTE, ton: 'attente', erreur };
};

/**
 * Date d'un envoi, par ordre de confiance décroissant (`DI2a-4`) :
 * `meta.timestamp` → `delivery.endTime` → `delivery.startTime` → `createdAt`.
 * @param {Object} doc - Document `mail` mappé
 * @returns {Date|null}
 */
export const dateEnvoi = (doc) => {
  if (!doc) return null;
  const candidats = [
    doc.meta && doc.meta.timestamp,
    doc.delivery && doc.delivery.endTime,
    doc.delivery && doc.delivery.startTime,
    doc.createdAt
  ];
  for (const candidat of candidats) {
    const date = toDateValue(candidat);
    if (date) return date;
  }
  return null;
};

/**
 * Trie les envois du plus RÉCENT au plus ancien, SANS `orderBy` Firestore (donc sans index
 * composite à créer — `DI2a-4`). Les envois sans date exploitable sont classés EN FIN ; à date
 * égale, l'ordre d'origine est conservé (tri stable, garanti par l'index de départ).
 * @param {Array<Object>} docs - Documents `mail` mappés
 * @returns {Array<Object>} Nouveau tableau trié (l'entrée n'est jamais modifiée)
 */
export const trierParDate = (docs) => {
  const liste = Array.isArray(docs) ? docs : [];
  return liste
    .map((doc, index) => {
      const date = dateEnvoi(doc);
      return { doc, index, ms: date ? date.getTime() : null };
    })
    .sort((a, b) => {
      if (a.ms === null && b.ms === null) return a.index - b.index;
      if (a.ms === null) return 1;
      if (b.ms === null) return -1;
      if (a.ms !== b.ms) return b.ms - a.ms;
      return a.index - b.index;
    })
    .map((entree) => entree.doc);
};

/**
 * Applique les filtres d'écran (`DI2a-8`).
 *
 * « Hors production » inclut les envois **non tracés** (`meta.environment` absent) : un envoi dont
 * l'environnement n'est pas prouvé n'est pas « de production ». C'est la doctrine retenue
 * (« étiqueter, jamais cacher ») et le comportement figé par les tests.
 * @param {Array<Object>} docs - Documents `mail` mappés
 * @param {Object} [filtres] - `{statut, environnement}`
 * @param {string} [filtres.statut] - `tous` | `echecs` | `envoyes`
 * @param {string} [filtres.environnement] - `tous` | `production` | `hors-production`
 * @returns {Array<Object>} Envois retenus
 */
export const filtrerEnvois = (docs, { statut = 'tous', environnement = 'tous' } = {}) => {
  const liste = Array.isArray(docs) ? docs : [];
  return liste.filter((doc) => {
    if (statut !== 'tous') {
      const { ton } = traduireVerdict(doc && doc.delivery);
      if (statut === 'echecs' && ton !== 'echec') return false;
      if (statut === 'envoyes' && ton !== 'succes') return false;
    }

    if (environnement !== 'tous') {
      const valeur = doc && doc.meta ? doc.meta.environment : null;
      const estProduction = valeur === 'production';
      if (environnement === 'production' && !estProduction) return false;
      if (environnement === 'hors-production' && estProduction) return false;
    }

    return true;
  });
};

/**
 * Compteur de tête d'onglet : « n envois · m échecs » (`DI2a-8`).
 * @param {Array<Object>} docs - Documents `mail` mappés (filtrés ou non)
 * @returns {{total: number, envoyes: number, echecs: number, attente: number}}
 */
export const compteursEnvois = (docs) => {
  const liste = Array.isArray(docs) ? docs : [];
  let envoyes = 0;
  let echecs = 0;
  let attente = 0;
  for (const doc of liste) {
    const { ton } = traduireVerdict(doc && doc.delivery);
    if (ton === 'succes') envoyes += 1;
    else if (ton === 'echec') echecs += 1;
    else attente += 1;
  }
  return { total: liste.length, envoyes, echecs, attente };
};

/**
 * Badges d'un envoi (`DI2a-7`), calculés depuis `meta` seul.
 *
 * `meta.environment` ABSENT ⇒ « environnement non tracé » : l'écran ne dit JAMAIS « test », parce
 * que l'absence de trace n'est pas une preuve de test (mesure C12 : 76 documents dans ce cas).
 * @param {Object|null} meta - Champ `meta` du document `mail`
 * @returns {Array<{cle: string, label: string, classe: string}>} Badges à afficher (peut être vide)
 */
export const badgesEnvoi = (meta) => {
  const source = meta || {};
  const badges = [];
  const environnement = typeof source.environment === 'string' && source.environment.trim()
    ? source.environment
    : null;

  if (!environnement) {
    badges.push({ cle: 'environnement-non-trace', label: 'environnement non tracé', classe: CLASSE_NEUTRE });
  } else if (environnement !== 'production') {
    badges.push({ cle: `environnement-${environnement}`, label: `hors production (${environnement})`, classe: CLASSE_ATTENTE });
  }

  if (source.sentByUid) {
    badges.push({ cle: 'envoi-admin', label: 'envoyé par un admin', classe: CLASSE_INFO });
  }
  if (source.forced === true) {
    badges.push({ cle: 'renvoi-assume', label: 'renvoi assumé', classe: CLASSE_ATTENTE });
  }

  return badges;
};

/**
 * Adresses destinataires d'un envoi. Le champ `to` vit à la RACINE du document
 * (`writeMailDocument`, `functions/services/email-utils.js`) et peut être une chaîne ou un tableau.
 * @param {(string|Array<string>|null)} to - Champ `to` du document `mail`
 * @returns {Array<string>} Adresses non vides
 */
export const destinatairesEnvoi = (to) => {
  if (Array.isArray(to)) return to.filter((adresse) => typeof adresse === 'string' && adresse.trim() !== '');
  if (typeof to === 'string' && to.trim() !== '') return [to];
  return [];
};

/**
 * Aperçu TEXTE d'un corps HTML : le balisage est retiré, les entités courantes décodées, les
 * espaces compressés, puis le texte est tronqué proprement (jamais au milieu d'un mot).
 * L'aperçu sert au détail replié : c'est le seul endroit où l'on parle du contenu SANS l'afficher.
 * @param {*} html - Champ `message.html` (peut être absent ou d'un autre type)
 * @param {number} [maxLength] - Longueur maximale de l'aperçu
 * @returns {string} Texte lisible (chaîne vide si le corps est inexploitable)
 */
export const apercuTexte = (html, maxLength = 160) => {
  if (typeof html !== 'string' || html.trim() === '') return '';

  const sansBalises = html
    .replace(/<style[\s\S]*?<\/style>/gi, ' ')
    .replace(/<script[\s\S]*?<\/script>/gi, ' ')
    .replace(/<head[\s\S]*?<\/head>/gi, ' ')
    .replace(/<[^>]*>/g, ' ');

  const decode = sansBalises
    .replace(/&nbsp;/gi, ' ')
    .replace(/&amp;/gi, '&')
    .replace(/&lt;/gi, '<')
    .replace(/&gt;/gi, '>')
    .replace(/&quot;/gi, '"')
    .replace(/&#39;/gi, "'")
    .replace(/&apos;/gi, "'");

  const propre = decode.replace(/\s+/g, ' ').trim();
  if (propre.length <= maxLength) return propre;

  const coupe = propre.slice(0, maxLength + 1);
  const dernierEspace = coupe.lastIndexOf(' ');
  const tronque = dernierEspace > 0 ? coupe.slice(0, dernierEspace) : propre.slice(0, maxLength);
  return `${tronque.trimEnd()}…`;
};

/**
 * Détail TECHNIQUE d'un envoi (replié par défaut, `DI2a-7` option (ii)) : chaque ligne est rendue
 * telle quelle par l'écran, et les champs absents sont OMIS (jamais de « undefined » affiché).
 * @param {Object} doc - Document `mail` mappé
 * @returns {Array<{label: string, valeur: string, mono: boolean}>} Lignes à afficher
 */
export const detailsEnvoi = (doc) => {
  if (!doc) return [];
  const delivery = doc.delivery || {};
  const info = delivery.info || {};
  const lignes = [];
  const ajoute = (label, valeur, mono = false) => {
    if (valeur === null || valeur === undefined || valeur === '') return;
    lignes.push({ label, valeur: String(valeur), mono });
  };

  ajoute('Gabarit', doc.meta && doc.meta.templateName, true);
  ajoute('Catégorie', doc.meta && doc.meta.category);
  ajoute('Id du document', doc.id, true);
  ajoute('Erreur remontée', delivery.error);
  ajoute('Tentatives', delivery.attempts);
  ajoute('Tentative démarrée', delivery.startTime);
  ajoute('Tentative terminée', delivery.endTime);
  ajoute('Identifiant de message', info.messageId, true);
  ajoute('Acceptés', Array.isArray(info.accepted) ? info.accepted.join(', ') : info.accepted);
  ajoute('Refusés', Array.isArray(info.rejected) ? info.rejected.join(', ') : info.rejected);
  return lignes;
};
